import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailAutoSendEmailCampaign, TAutosendSubtype } from '@feathr/blackbox';
import type { IRadioOption } from '@feathr/components';
import { Form } from '@feathr/components';
import { moment, TimeFormat } from '@feathr/hooks';
import type { TValidateGrouped } from '@feathr/rachis';

import type { IActionErrors } from '../../../CampaignSummary';
import MailrailAutomationSchedule from '../../MailrailAutomationSchedule/MailrailAutomationSchedule';
import MailrailPredicatesConfig from '../../MailrailPredicatesConfig/MailrailPredicatesConfig';
import TriggerTypeRadios from '../../TriggerTypeRadios/TriggerTypeRadios';

interface IProps {
  campaign: MailrailAutoSendEmailCampaign;
  disabled: boolean;
}

interface IErrors extends TValidateGrouped {
  delay_seconds?: string[];
  predicates?: IActionErrors[];
}

export function validateStepAutomation(campaign: MailrailAutoSendEmailCampaign): IErrors {
  return campaign.validate<IErrors>(['delay_seconds', 'predicates'], false, 'grouped').errors;
}

function MailrailCampaignStepAutomation({ campaign, disabled }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const subtype = campaign.get('subtype');
  const defaults = campaign.getDefaults();
  const isTimeSubtype = campaign.get('subtype') === 'time';

  const triggerTypeOptions: Array<IRadioOption<TAutosendSubtype>> = [
    {
      id: 'activity',
      name: t('Activity happened'),
    },
    {
      id: 'update',
      name: t('Field changed'),
    },
    {
      id: 'time',
      name: t('Date triggered'),
    },
  ];

  function handleSubtypeChange(newValue?: string): void {
    if (!newValue) {
      return;
    }

    if (newValue === 'time') {
      // Time subtype doesn't support the "send now" option, so a start-date is required
      let newStart = campaign.get('date_start');
      if (!newStart) {
        const newStartMoment = moment.utc().add(15, 'minutes');
        const roundedMinutes = Math.ceil(newStartMoment.minute() / 5) * 5;
        newStartMoment.minute(roundedMinutes).second(0);
        newStart = newStartMoment.format(TimeFormat.isoDateTime);
      }
      // Even though the backend will ignore delay_seconds and cooldown_seconds when subtype is time,
      // reset them to zero so the data is more representative of the UI state.
      campaign.set({
        date_start: newStart,
        bulk_send_enabled: false,
        delay_seconds: 0,
        predicates_mode: 'match_any',
        repeat_allowed: false,
        repeat_cooldown_seconds: 0,
        subtype: newValue,
      });
    } else {
      campaign.set({
        delay_seconds: defaults.delay_seconds,
        predicates_mode: defaults.predicates_mode,
        subtype: newValue as TAutosendSubtype,
      });
    }

    // Clear the existing predicates if trigger type is modified
    campaign.set({ predicates: [] });
  }

  const validationErrors = validateStepAutomation(campaign);

  return (
    <Form
      description={t(
        'When should the email in this campaign be sent to people? Configure as many triggers as you like. Activity triggers (like URL visits) can only be combined with other activities. Attribute triggers (like job title) can only be combined with other attributes.',
      )}
      label={t('Edit Campaign: Targeting')}
      title={t('Automation')}
      width={'wide'}
    >
      <TriggerTypeRadios
        disabled={disabled}
        handleSubtypeChange={handleSubtypeChange}
        subtype={subtype}
        triggerTypeOptions={triggerTypeOptions}
      />

      <MailrailPredicatesConfig campaign={campaign} disabled={disabled} />

      {!isTimeSubtype && (
        <MailrailAutomationSchedule
          campaign={campaign}
          context={'singular'}
          disabled={disabled}
          validationErrors={validationErrors}
        />
      )}
    </Form>
  );
}

export default observer(MailrailCampaignStepAutomation);
